/*
 * Hash — render a cryptographic value in IBM Plex Mono, truncated, with
 * copy-to-clipboard and an optional drill-in to its proof.
 *
 * Every hash, key id, request id, or section ref the console shows goes
 * through this. The all-zeros sentinel renders as a muted "— not yet acked"
 * so "queued vs delivered" is never mistaken for a real hash.
 */

import { useState } from "react";
import { ZERO_HASH } from "../api/types";

interface HashProps {
  value: string;
  /** Optional human label rendered before the value. */
  label?: string;
  /** Chars to keep at head/tail when truncating. 0 => show full. */
  head?: number;
  tail?: number;
  /** Open the ProvabilityDrawer for this value (caller wires the drawer). */
  onInspect?: () => void;
  /** Treat an all-zeros value as the "not yet acked" sentinel. */
  zeroMeans?: string;
  /** Accent the value with a semantic colour var, e.g. "var(--teal)". */
  accent?: string;
}

function isZero(v: string): boolean {
  const h = (v ?? "").replace(/^0x/, "");
  return h.length > 0 && /^0+$/.test(h);
}

export function Hash({
  value,
  label,
  head = 10,
  tail = 8,
  onInspect,
  zeroMeans = "not yet acked (queued)",
  accent,
}: HashProps) {
  const [copied, setCopied] = useState(false);

  const zero = value === ZERO_HASH || isZero(value);
  const truncated =
    head + tail > 0 && value.length > head + tail + 1
      ? `${value.slice(0, head)}…${value.slice(-tail)}`
      : value;

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard blocked — silent; the full value is still in the title */
    }
  }

  if (zero) {
    return (
      <span
        className="mono"
        style={{ color: "var(--fg-faint)", fontStyle: "italic", fontSize: "12.5px" }}
        title={`all-zeros sentinel — ${zeroMeans}`}
      >
        {label && <span style={{ marginRight: 6 }}>{label}</span>}— {zeroMeans}
      </span>
    );
  }

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        fontFamily: "var(--font-mono)",
        fontSize: "12.5px",
      }}
    >
      {label && <span style={{ color: "var(--fg-faint)" }}>{label}</span>}
      {onInspect ? (
        <button
          type="button"
          onClick={onInspect}
          title={`${value}\n(inspect proof)`}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            color: accent ?? "var(--fg)",
            fontFamily: "inherit",
            fontSize: "inherit",
            textDecoration: "underline",
            textDecorationColor: "var(--hair-strong)",
            textUnderlineOffset: "3px",
          }}
        >
          {truncated}
        </button>
      ) : (
        <span title={value} style={{ color: accent ?? "var(--fg)" }}>
          {truncated}
        </span>
      )}
      <button
        type="button"
        onClick={copy}
        aria-label={copied ? "Copied" : "Copy full value"}
        title="Copy full value"
        style={{
          background: "none",
          border: "1px solid var(--hair)",
          borderRadius: "4px",
          color: copied ? "var(--teal)" : "var(--fg-faint)",
          fontFamily: "var(--font-mono)",
          fontSize: "10px",
          lineHeight: 1,
          padding: "3px 5px",
          cursor: "pointer",
        }}
      >
        {copied ? "copied" : "copy"}
      </button>
    </span>
  );
}
